// Canonical one-letter handedness display, shared across every player table
// (players, free agency, draft, draft history, lineups, depth chart, …).
//
// The `Handedness` enum / DB column is **0-indexed**: 0=Right, 1=Left,
// 2=Switch — mirroring the legacy `cStats.GetHands`, which wraps a 0-indexed
// `Array("R", "L", "S")`. Switch hitters display as **"S"** site-wide (NOT
// "B" — that was a one-off deviation on the FA page, now removed).
//
// Two wire shapes feed this:
//   - integer enum value (0/1/2) — view-shaped row DTOs like LeaguePlayerRow,
//     LeagueDraftRow, lineup rows project `(int)` directly.
//   - string enum name ("right"/"left"/"switch") — DTOs exposing the
//     `Handedness` enum (e.g. PlayerBio.bats) serialize it as a string.
//
// Both map to the same R/L/S. Unknown / null / out-of-range → '' so callers
// can choose their own blank treatment (e.g. players appends '—').
const HAND_LETTERS = ['R', 'L', 'S'] as const

export function handAbbr(value: number | string | null | undefined): string {
  if (value == null) return ''
  if (typeof value === 'number') {
    return HAND_LETTERS[value] ?? ''
  }
  switch (value.trim().toLowerCase()) {
    case 'right':
    case 'r':
      return 'R'
    case 'left':
    case 'l':
      return 'L'
    case 'switch':
    case 's':
      return 'S'
    default:
      return ''
  }
}

// Pitcher-specific hand label: `RHP` / `LHP` reads clearer than a bare R/L on a
// probable-starter line (lineups, dashboard next-game, team-view games). Falls
// back to the plain abbr for switch/unknown; '' when there's no hand to show.
export function pitcherHandAbbr(value: number | string | null | undefined): string {
  const abbr = handAbbr(value)
  if (abbr === 'R') return 'RHP'
  if (abbr === 'L') return 'LHP'
  return abbr
}

// The B/T token on the identity anchors (free agency, players, draft, roster)
// and the player-card vitals line.
//
// **Pitchers show their throwing hand only, as `RHP` / `LHP`** — they never bat
// in BaseHit (a lineup always carries a DH; there is no league setting that
// makes pitchers hit), so a `R/R` on a pitcher spends a slot on a number that
// never comes up. The batting-rating columns already hide for pitchers on these
// same pages; this is the identity line catching up. Reuses `pitcherHandAbbr`,
// the same label already used on probable-starter lines.
//
// Batters keep the merged `B/T` (see the B/T column convention). Each side falls
// back to an em-dash independently, so a half-known batter reads `R/—` rather
// than collapsing the whole token.
//
// `throws` is only ever Right or Left — there are no switch-throwers in the data
// (verified 2026-07-28 vs csfbl-game-test: 0 of 1.45M rows). A pitcher rendering
// a bare 'S' therefore means the data is wrong, not that we grew a new case.
export function handToken(
  bats: number | string | null | undefined,
  throws: number | string | null | undefined,
  isPitcher: boolean,
): string {
  if (isPitcher) return pitcherHandAbbr(throws) || '—'
  return `${handAbbr(bats) || '—'}/${handAbbr(throws) || '—'}`
}
